import type { SessionToolName } from '@/lib/code-sessions/session-tools';
import type { McpToolRef } from '@/lib/mcp-servers/tool-names';

import { OPENCODE_CODE_MODE_TOOL_NAME, parseMcpToolName } from '@/lib/mcp-servers/tool-names';
import { getRecord } from '@/lib/utils/unknown';

type BuiltInToolStepKind =
  | 'read'
  | 'edit'
  | 'write'
  | 'shell'
  | 'script'
  | 'grep'
  | 'glob'
  | 'web'
  | 'share'
  | 'agent'
  | 'message'
  | 'spawn'
  | 'interrupt'
  | 'sessions'
  | 'transcript'
  | 'wait'
  | 'skill'
  // A pull request page's chat (`session-tools/pr-page.ts`).
  | 'focus'
  | 'comment'
  | 'mark'
  | 'cubic'
  | 'propose';

/** What an agent tool call did, as every /code client labels its steps. */
export type ToolStepKind = BuiltInToolStepKind | 'mcp';

// cubic's session tools (`session-tools/index.ts`), every one named, so a new
// tool picks how its steps read.
const SESSION_TOOL_STEP_KIND: Readonly<Record<SessionToolName, BuiltInToolStepKind>> = {
  // The web transcript shows it as a card instead.
  suggest_environment: 'shell',
  open_pull_request: 'shell',
  // Its file shows under the turn's reply.
  share_file: 'share',
  // The web transcript shows the page instead.
  show_page: 'share',
  schedule_check_in: 'shell',
  send_message: 'message',
  followup_task: 'message',
  spawn_side_agent: 'spawn',
  interrupt_agent: 'interrupt',
  start_session: 'spawn',
  list_sessions: 'sessions',
  read_session: 'transcript',
  wait_agent: 'wait',
  report_findings: 'shell',
  // The page's sidebar shows them as it showed its chat's own tools.
  focus_code: 'focus',
  draft_pr_comment: 'comment',
  set_pr_file_viewed: 'mark',
  pr_summary: 'cubic',
  pr_review_plan: 'cubic',
  pr_changed_files: 'cubic',
  pr_changed_file: 'cubic',
  pr_search: 'cubic',
  pr_repo_memory: 'cubic',
  pr_threads: 'cubic',
  pr_thread: 'cubic',
  pr_checks: 'cubic',
  list_custom_agents: 'cubic',
  propose_custom_agent_change: 'propose',
  search_code_docs: 'web',
};

const TOOL_STEP_KIND: Readonly<Record<string, BuiltInToolStepKind>> = {
  read: 'read',
  edit: 'edit',
  patch: 'edit',
  write: 'write',
  shell: 'shell',
  bash: 'shell',
  [OPENCODE_CODE_MODE_TOOL_NAME]: 'script',
  grep: 'grep',
  glob: 'glob',
  webfetch: 'web',
  websearch: 'web',
  subagent: 'agent',
  skill: 'skill',
  ...SESSION_TOOL_STEP_KIND,
};

/** The agent's tool for asking the person: clients show a question, not a step. */
export const QUESTION_TOOL_NAME = 'question';

/** The tool a cubic bot run hands back its result with: clients show the run's result, not a step. */
export const SUBMIT_RESULT_TOOL_NAME = 'submit_result';

/**
 * The tool an auto-fix run records a batch of its verdicts with as each batch lands, before its
 * last batch goes in `submit_result`.
 */
export const SUBMIT_BATCH_TOOL_NAME = 'submit_batch';

/** Unknown tools also fall back to the shell kind; only these ran a command. */
export const COMMAND_TOOL_NAMES: ReadonlySet<string> = new Set(['shell', 'bash']);

// A command continues on the next line after a backslash no other backslash
// escapes, and a new one starts on a new line or after `;`, `&` or `|`. A word
// inside a quoted argument (`rg "git push"`) does not count.
const LINE_CONTINUATION = /(?<!\\)((?:\\\\)*)\\\r?\n/g;
const COMMAND_SEPARATOR = /[\n;&|]/;
// Also after `(`, where a subshell or `$(…)` starts a command inside another.
const NESTED_COMMAND_SEPARATOR = /[\n;&|(]/;

// `git push`, after any global options (`-C <dir>`), or a `gh pr` command that
// opens or changes a pull request, at the start of a command, after any
// variable assignments. `-C` and `-c` always take the next word, so every word
// reads one way and the check stays linear in the command's length: a pattern
// that let `-C` stand alone too took exponential time to reject `git -C -C …`.
const PUSH_COMMAND =
  /^\s*(?:[A-Z_a-z]\w*=\S*\s+)*(?:git(?:\s+(?:-[Cc]\s+\S+|-(?![Cc](?:\s|$))\S+))*\s+push\b|gh\s+pr\s+(?:create|edit|ready|merge)\b)/;

export const TOOL_STEP_LABEL: Record<BuiltInToolStepKind, string> = {
  read: 'Read',
  edit: 'Edited',
  write: 'Created',
  shell: 'Ran',
  script: 'Ran script',
  grep: 'Searched',
  glob: 'Listed',
  web: 'Fetched',
  share: 'Shared',
  agent: 'Delegated',
  message: 'Messaged',
  spawn: 'Started',
  interrupt: 'Interrupted',
  sessions: 'Listed sessions',
  transcript: 'Read session',
  wait: 'Waited for messages',
  skill: 'Loaded skill',
  focus: 'Showed',
  comment: 'Drafted a comment on',
  mark: 'Marked files',
  cubic: 'Read from cubic',
  propose: 'Proposed',
};

/** The same labels while the step is still running. */
export const TOOL_STEP_RUNNING_LABEL: Record<ToolStepKind, string> = {
  read: 'Reading',
  edit: 'Editing',
  write: 'Creating',
  shell: 'Running',
  script: 'Running script',
  grep: 'Searching',
  glob: 'Listing',
  web: 'Fetching',
  share: 'Sharing',
  agent: 'Delegating',
  message: 'Messaging',
  spawn: 'Starting',
  interrupt: 'Interrupting',
  sessions: 'Listing sessions',
  transcript: 'Reading session',
  wait: 'Waiting for messages',
  skill: 'Loading skill',
  focus: 'Showing',
  comment: 'Drafting a comment on',
  mark: 'Marking files',
  cubic: 'Reading from cubic',
  propose: 'Proposing',
  mcp: 'Calling',
};

/** Whether `value` names a step kind, which data another version of the code wrote may not. */
export function isToolStepKind(value: string): value is ToolStepKind {
  return Object.hasOwn(TOOL_STEP_RUNNING_LABEL, value);
}

/**
 * The label a step's row shows: while the step runs, its kind's running label
 * ("Starting", "Reading"). A label that names MCP calls has no tense, so it
 * stays as it is.
 */
export function shownToolStepLabel(step: {
  kind: ToolStepKind;
  label: string;
  running?: boolean;
}): string {
  const { kind } = step;
  return step.running === true && kind !== 'mcp' && step.label === TOOL_STEP_LABEL[kind]
    ? TOOL_STEP_RUNNING_LABEL[kind]
    : step.label;
}

function builtInToolStepKind(toolName: string): BuiltInToolStepKind {
  return TOOL_STEP_KIND[toolName] ?? 'shell';
}

export function toolStepKind(toolName: string): ToolStepKind {
  return parseMcpToolName(toolName) ? 'mcp' : builtInToolStepKind(toolName);
}

/**
 * An MCP step's label: `MCP · <server>: <tool>`, or `; `-joined per server
 * when the call used several.
 */
export function mcpLabel(calls: readonly McpToolRef[]): string {
  const byServer = new Map<string, string[]>();
  for (const { server, tool } of calls) {
    byServer.set(server, [...(byServer.get(server) ?? []), tool]);
  }
  const servers = [...byServer].map(([server, tools]) => `${server}: ${tools.join(', ')}`);
  return `MCP · ${servers.join('; ')}`;
}

/**
 * A step's label: its kind's, or `MCP · <server>: <tool>` for a flat MCP tool.
 * A Code Mode wrapper reads "Ran script"; its recorded children name the tools.
 */
export function toolStepLabel(toolName: string): string {
  const mcp = parseMcpToolName(toolName);
  return mcp ? mcpLabel([mcp]) : TOOL_STEP_LABEL[builtInToolStepKind(toolName)];
}

/** The first non-empty string among `keys` of an object input. */
export function stringInput(input: unknown, keys: readonly string[]): string {
  if (typeof input !== 'object' || input === null) {
    return '';
  }
  const record = input as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return '';
}

/**
 * Whether a tool call ran `git push` or `gh pr`. The session can then have a
 * pull request though the turn left no file changed: the agent commits as it goes.
 */
export function isPushCommand(toolName: string, input: unknown): boolean {
  if (!COMMAND_TOOL_NAMES.has(toolName)) {
    return false;
  }
  const script = stringInput(input, ['command']).replace(LINE_CONTINUATION, '$1 ');
  // Whole commands first, so a word holding a `$(…)` stays whole
  // (`git -C "$(pwd)" push`), then the commands that start inside one.
  return [COMMAND_SEPARATOR, NESTED_COMMAND_SEPARATOR].some((separator) =>
    script.split(separator).some((command) => PUSH_COMMAND.test(command)),
  );
}

// `description` after the keys above: it is a subagent call's label, and the
// one other tool a session allows with one names its step otherwise
// (`TOOL_INPUT_DETAIL_KEYS`).
const INPUT_DETAIL_KEYS = [
  'filePath',
  'path',
  // The changed file a pull request page's tool shows or comments on (`session-tools/pr-page.ts`).
  'filename',
  'command',
  'pattern',
  'query',
  'url',
  'description',
  // The agent a call messages, starts, stops or reads: "Messaged side-1", "Started fix_tests".
  'target',
  'task_name',
] as const;

/** Tools whose step names something other than what `INPUT_DETAIL_KEYS` finds. */
const TOOL_INPUT_DETAIL_KEYS: Readonly<Record<string, readonly string[]>> = {
  // OpenCode's skill tool names the skill by `id`, a key other tools use for
  // ids no one reads.
  skill: ['id'],
  show_page: ['title'],
  pr_thread: ['threadId'],
  // Its `description` is the custom agent's, which names nothing short.
  propose_custom_agent_change: ['title', 'agentId'],
};

function fileViewedInputDetail(input: unknown): string {
  const record = getRecord(input);
  if (!record) {
    return '';
  }
  const files = Array.isArray(record.filenames)
    ? record.filenames.filter(
        (filename): filename is string =>
          typeof filename === 'string' && filename.trim().length > 0,
      )
    : [];
  const detail = files.join(', ');
  if (typeof record.viewed !== 'boolean') {
    return detail;
  }
  const target = record.filenames === undefined ? 'current file' : detail;
  return target ? `${target} as ${record.viewed ? 'viewed' : 'unviewed'}` : '';
}

/**
 * What a step names from its tool input: the file, command, pattern, query,
 * URL, subagent task or messaged agent, or the skill it loads.
 */
export function toolInputDetail(toolName: string | undefined, input: unknown): string {
  if (toolName === 'set_pr_file_viewed') {
    return fileViewedInputDetail(input);
  }
  if (typeof input === 'string') {
    return input;
  }
  return stringInput(
    input,
    (toolName === undefined ? undefined : TOOL_INPUT_DETAIL_KEYS[toolName]) ?? INPUT_DETAIL_KEYS,
  );
}

/** Patch calls carry their filenames in file headers, not a top-level path. */
export function toolStepDetail(
  toolName: string,
  input: unknown,
): {
  detail: string;
  /** A single file the UI can open; undefined for non-file tools and multi-file patches. */
  openPath: string | undefined;
} {
  if (toolName !== 'patch') {
    const detail = toolInputDetail(toolName, input);
    const kind = toolStepKind(toolName);
    return {
      detail,
      openPath:
        detail && (kind === 'read' || kind === 'edit' || kind === 'write') ? detail : undefined,
    };
  }
  const patch = stringInput(input, ['patchText', 'patch']);
  const paths: string[] = [];
  // A `Move to` header renames the file above it, so the file opens at its destination.
  const files: string[] = [];
  for (const [, header, rawPath] of patch.matchAll(
    /^\*\*\* (Add File|Update File|Delete File|Move to): (.+)\r?$/gm,
  )) {
    const path = rawPath!.trim();
    if (!path) {
      continue;
    }
    paths.push(path);
    if (header === 'Move to' && files.length > 0) {
      files[files.length - 1] = path;
    } else {
      files.push(path);
    }
  }
  const uniqueFiles = [...new Set(files)];
  return {
    detail: [...new Set(paths)].join(', '),
    openPath: uniqueFiles.length === 1 ? uniqueFiles[0] : undefined,
  };
}
